/**
 * FuarAlaniDetayPage.jsx — bir fuar alanının tek ekrandaki görünümü (backlog #51, 2026-09-20)
 * Route: /Management/FairAreas/:fuarAlaniId
 *
 * FirmaDetayPage.jsx (Modules/Firmalar) deseni üzerine kurulu: Velzon profile-wid başlık
 * (initials avatar + ad + StatBox sayaçlar + geri/düzenle düğmeleri), altında ?tab= ile
 * tutulan Nav sekmeleri, her sekme yalnızca aktifken monte olur.
 *
 * Sekmeler, halihazırda var olan veriden:
 *   Genel Bilgiler — ad (GET /fuar/alani/{id})
 *   Salonlar       — FuarAlaniSalonlariCard, zaten gömülebilir (Card, kendi Container'ı yok)
 *   Fuarlar        — GET /fuar/list, fuarAlaniId'ye göre ekranda filtrelenir (precedent:
 *                    fuarKuralService._fairsOfVenue)
 *   Krokiler       — salon seçici + KrokiVectorEmbed (fairgroundId + salonId yeterli, #27/#28)
 *                    ⚠️ KALDIRILDI (yorum satırı) 2026-09-26, backlog #193: Salon Planı artık
 *                    Salonlar sekmesindeki kalem düğmesiyle kendi sayfasında tam düzenleme
 *                    modunda açılıyor (SalonPlaniDuzenlePage); hızlı bakış için haritalı modal da
 *                    orada. Aşağıdaki sekme + TabPane silinmedi, yorum satırı olarak duruyor.
 *
 * 2026-09-20 notu: backlog #99'un getireceği ek fuar alanı verisi (logo, künye, konum,
 * fotoğraflar, organizasyon şeması) BİLEREK burada YOK — #99 backend'de henüz yok, o
 * gelince Genel Bilgiler sekmesi genişler. Gross m² ve salon hizmetleri (#100/#101) de
 * bu sayfada değil, Salonlar listesi sayfasında kalıyor.
 *
 * 2026-10-20 (#51, backend #99 hazır): Genel Bilgiler artık logo, künye, adres ve harita konumunu; yeni Fotoğraflar sekmesi galeriyi; yeni Organizasyon
 * sekmesi organizasyon şemasını (ağaç) gösterir. Hepsi GET /fuar/alani/{id}'nin zaten döndürdüğü veriden (yeni istek yok), SALT OKUNUR
 * (Components/FairAreas/FuarAlaniBilgiKartlari). page.fuar.yonetim olan kullanıcı bunları BURADAN düzenler (künye + adres + konum + logo bir pencerede, fotoğraf yükle / kapak / başlık / kaldır, organizasyon
 * şemasında kişi ekle / düzenle / kaldır); yazma sonrası sayfa fuar alanını yeniden okur. İzni olmayan salt okur görür.
 *
 * Routing: bu route WAS düzenleme formu (FuarAlaniFormPage) — düzenleme artık
 * /Management/FairAreas/:fuarAlaniId/edit'te (allRoutes.js).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Container, Row, Col, Card, CardBody, Button, Spinner, Alert, Table,
  // WAS: ..., TabPane, Input, Label — Input yalnızca kaldırılan Krokiler sekmesindeydi (#193).
  Nav, NavItem, NavLink, TabContent, TabPane, Label,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { initials, StatBox } from "../../../Components/Common/DetailHeaderBits";
import FuarAlaniSalonlariCard from "../../../Components/FairAreas/FuarAlaniSalonlariCard";
import {
  FuarAlaniKunyeCard, FuarAlaniKonumCard, FuarAlaniFotograflarCard, FuarAlaniOrgSemasiCard,
} from "../../../Components/FairAreas/FuarAlaniBilgiKartlari";
import { firmaFileSrc } from "../../../services/firmaMediaService";
import { useYetki, SAYFA_IZNI } from "../../../services/rbacService";
// Backlog #193: yalnızca kaldırılan Krokiler sekmesi kullanıyordu.
// import KrokiVectorEmbed from "../../../Components/Kroki/KrokiVectorEmbed";
import { getFuarAlani } from "../../../services/fuarAlaniSalonService";
import { getSalonlarByFuarAlani } from "../../../services/krokiService";
import { fuarService } from "../../../services/fuarService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const TABS = [
  { key: "genel",    get label() { return i18n.t("yonetim:fuarAlaniDetay.genelBilgiler"); }, icon: "bx-info-circle" },
  { key: "salonlar", get label() { return i18n.t("yonetim:fuarAlaniDetay.salonlar"); },       icon: "bx-buildings" },
  { key: "fuarlar",  get label() { return i18n.t("yonetim:fuarAlaniDetay.fuarlar"); },        icon: "bx-calendar" },
  // #51: the venue's photo gallery and organisation chart (backend #99).
  { key: "fotograflar",  get label() { return i18n.t("yonetim:fuarAlaniDetay.fotograflar"); },  icon: "bx-images" },
  { key: "organizasyon", get label() { return i18n.t("yonetim:fuarAlaniDetay.organizasyon"); }, icon: "bx-sitemap" },
  // Backlog #193 (2026-09-26): Salon Planı tam düzenlemesi kendi sayfasına taşındı
  // (Salonlar sekmesi → kalem düğmesi). ?tab=kroki ile gelen eski bağlantılar Genel Bilgiler'e düşer.
  // { key: "kroki",    label: "Krokiler",       icon: "bx-map-alt" },
];

const formatDate = (v) => (v ? new Date(v).toLocaleDateString(currentLocaleTag()) : "—");

export default function FuarAlaniDetayPage() {
  const { t: tx } = useTranslation("yonetim");
  const { fuarAlaniId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const tabParam = searchParams.get("tab");
  const activeTab = TABS.some((t) => t.key === tabParam) ? tabParam : "genel";

  const [fuarAlani, setFuarAlani] = useState(null);
  const [halls, setHalls] = useState([]);
  const [allFairs, setAllFairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Backlog #193: yalnızca kaldırılan Krokiler sekmesinin salon seçicisi.
  // const [selectedHallId, setSelectedHallId] = useState(null);

  useEffect(() => { document.title = i18n.t("yonetim:fuarAlaniDetay.fuarAlaniDetayFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [alani, hallRows, fairRows] = await Promise.all([
          getFuarAlani(fuarAlaniId),
          getSalonlarByFuarAlani(fuarAlaniId),
          fuarService.getFuarList(),
        ]);
        if (ignore) return;
        setFuarAlani(alani);
        setHalls(hallRows || []);
        setAllFairs(Array.isArray(fairRows) ? fairRows : []);
      } catch (e) {
        if (!ignore) setError(e.status === 404 ? i18n.t("yonetim:fuarAlaniDetay.fuarAlaniBulunamadi") : (e.message || i18n.t("yonetim:fuarAlaniDetay.fuarAlaniOkunamadi")));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarAlaniId]);

  // Precedent: fuarKuralService._fairsOfVenue — GET /fuar/list'i ekranda fuarAlaniId'ye göre süz.
  const fairsHere = useMemo(
    () => allFairs
      .filter((f) => Number(f.fuarAlaniId) === Number(fuarAlaniId))
      .sort((a, b) => String(b.fuarStartDate || "").localeCompare(String(a.fuarStartDate || ""))),
    [allFairs, fuarAlaniId],
  );

  // Backlog #193: yalnızca kaldırılan Krokiler sekmesi.
  // const selectedHall = halls.find((h) => String(h.salonId) === String(selectedHallId)) ?? halls[0] ?? null;

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const name = fuarAlani?.fuarAlaniName;

  // #51: the edit controls of the extra data need page.fuar.yonetim (a view decision only: the backend checks every write again).
  const canEdit = useYetki(SAYFA_IZNI.FUAR_YONETIM) === true;
  // After a write: read the venue again so every card shows what the backend holds (the answers of the writes differ in shape).
  const yenile = async () => {
    try {
      setFuarAlani(await getFuarAlani(fuarAlaniId));
    } catch (e) {
      setError(e.message || i18n.t("yonetim:fuarAlaniDetay.fuarAlaniOkunamadi"));
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("fuarAlaniDetay.fuarAlaniDetay")} pageTitle={tx("fuarAlaniDetay.fuarAlanlari")} />

        {error && <Alert color="danger">{error}</Alert>}

        {/* ─── Kimlik başlığı ──────────────────────────────────────────────── */}
        <Card className="mb-0">
          <CardBody className="pb-2">
            {loading ? (
              <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>
            ) : (
              <Row className="g-3 align-items-center">
                <Col xs="auto">
                  <div className="avatar-md">
                    {fuarAlani?.fuarAlaniLogoUrl ? (
                      <img
                        src={firmaFileSrc(fuarAlani.fuarAlaniLogoUrl)} alt={name || ""} className="rounded"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <span className="avatar-title rounded bg-primary-subtle text-primary fs-22 fw-semibold">
                        {initials(name)}
                      </span>
                    )}
                  </div>
                </Col>

                <Col>
                  <h4 className="mb-1">{name || tx("fuarAlaniDetay.fuarAlani", { fuarAlaniId })}</h4>
                  <p className="text-muted mb-0 fs-13">{tx("fuarAlaniDetay.fuarAlani2")}{fuarAlaniId}</p>
                </Col>

                <Col xs={12} xl="auto">
                  <div className="d-flex align-items-center flex-wrap gap-4">
                    <StatBox icon="bx-buildings" value={halls.length}    label={tx("fuarAlaniDetay.salon")} />
                    <StatBox icon="bx-calendar"  value={fairsHere.length} label={tx("fuarAlaniDetay.fuar")} />
                    <Button
                      color="light" size="sm"
                      onClick={() => navigate("/Management/FairAreas")}
                    >
                      <i className="bx bx-arrow-back me-1" />{tx("fuarAlaniDetay.listeyeDon")}
                    </Button>
                    <Button
                      color="warning" size="sm" outline
                      onClick={() => navigate(`/Management/FairAreas/${fuarAlaniId}/edit`)}
                    >
                      <i className="bx bx-edit me-1" />{tx("common:duzenle")}
                    </Button>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>

          <Nav tabs className="nav-tabs-custom border-bottom-0 px-3">
            {TABS.map((t) => (
              <NavItem key={t.key}>
                <NavLink
                  href="#"
                  className={`fw-semibold ${activeTab === t.key ? "active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                >
                  <i className={`bx ${t.icon} me-1 align-middle`} />
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Card>

        {/* ─── Sekme içerikleri ────────────────────────────────────────────── */}
        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="genel">
            {activeTab === "genel" && (
              loading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : (
                <Card>
                  <CardBody>
                    <Row className="g-3">
                      <Col md={6}>
                        <Label className="text-muted">{tx("fuarAlaniDetay.fuarAlaniAdi")}</Label>
                        <div className="fw-semibold fs-15">{name || "—"}</div>
                      </Col>
                    </Row>
                    {/* WAS (until #51, 2026-10-20): the "these fields will come with backend #99" note; the fields are below now.
                    <div className="text-muted small mt-3">
                      {tx("fuarAlaniDetay.adDisindakiAlanlarLogoKunye")}{" "}<Link to={`/Management/FairAreas/${fuarAlaniId}/edit`}>{tx("common:duzenle")}</Link>.
                    </div>
                    */}
                  </CardBody>
                </Card>
              )
            )}
            {activeTab === "genel" && !loading && fuarAlani && (
              <Row className="g-3 mt-0">
                <Col lg={6}><FuarAlaniKunyeCard fuarAlaniId={fuarAlaniId} alani={fuarAlani} canEdit={canEdit} onChanged={yenile} /></Col>
                <Col lg={6}><FuarAlaniKonumCard fuarAlaniId={fuarAlaniId} alani={fuarAlani} canEdit={canEdit} onChanged={yenile} /></Col>
              </Row>
            )}
          </TabPane>

          <TabPane tabId="salonlar">
            {activeTab === "salonlar" && <FuarAlaniSalonlariCard fuarAlaniId={fuarAlaniId} />}
          </TabPane>

          <TabPane tabId="fuarlar">
            {activeTab === "fuarlar" && (
              <Card>
                <CardBody className="p-0">
                  {loading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : fairsHere.length === 0 ? (
                    <div className="text-muted text-center py-4">{tx("fuarAlaniDetay.buFuarAlanindaDuzenlenmisFuar")}</div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="ps-3">{tx("fuarAlaniDetay.fuar_2")}</th>
                            <th>{tx("fuarAlaniDetay.tarih")}</th>
                            <th className="text-end pe-3" style={{ width: 100 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {fairsHere.map((f) => (
                            <tr key={f.fuarId}>
                              <td className="ps-3 fw-semibold">{f.fuarName}</td>
                              <td className="text-muted small">
                                {formatDate(f.fuarStartDate)} – {formatDate(f.fuarEndDate)}
                              </td>
                              <td className="text-end pe-3">
                                <Link to={`/Management/Fairs/${f.fuarId}`} className="btn btn-sm btn-soft-info">
                                  <i className="bx bx-show" />
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </TabPane>

          <TabPane tabId="fotograflar">
            {activeTab === "fotograflar" && (
              loading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : (
                <FuarAlaniFotograflarCard fuarAlaniId={fuarAlaniId} photos={fuarAlani?.photos || []} canEdit={canEdit} onChanged={yenile} />
              )
            )}
          </TabPane>

          <TabPane tabId="organizasyon">
            {activeTab === "organizasyon" && (
              loading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : (
                <FuarAlaniOrgSemasiCard fuarAlaniId={fuarAlaniId} nodes={fuarAlani?.orgChart || []} canEdit={canEdit} onChanged={yenile} />
              )
            )}
          </TabPane>

          {/* Backlog #193 (2026-09-26): Krokiler sekmesi kaldırıldı — yerine Salonlar sekmesindeki
              kalem düğmesi (SalonPlaniDuzenlePage). Silinmedi, yorum satırı:
          <TabPane tabId="kroki">
            {activeTab === "kroki" && (
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : halls.length === 0 ? (
                    <div className="text-muted text-center py-4">Bu fuar alanında kayıtlı salon yok.</div>
                  ) : (
                    <>
                      <Row className="g-2 align-items-end mb-3">
                        <Col md={5}>
                          <Label className="form-label">Salon</Label>
                          <Input
                            type="select"
                            value={selectedHall?.salonId ?? ""}
                            onChange={(e) => setSelectedHallId(e.target.value)}
                          >
                            {halls.map((h) => (
                              <option key={h.salonId} value={h.salonId}>
                                {h.salonName}{h.salonCode ? ` (${h.salonCode})` : ""}
                              </option>
                            ))}
                          </Input>
                        </Col>
                      </Row>
                      {selectedHall && (
                        <KrokiVectorEmbed
                          fairgroundId={fuarAlaniId}
                          hallId={selectedHall.salonId}
                          hallName={selectedHall.salonName}
                          height={520}
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            )}
          </TabPane>
          */}
        </TabContent>
      </Container>
    </div>
  );
}
